/**
 * policiesKillSwitchService — authFetch wrappers around the operator
 * /api/admin/ai/kill-switch/tenants/* surface.
 *
 * WQ-232 (Phase 4.9 α6). Operator-side equivalent of
 * tenantKillSwitchService — every mutating call takes a tenant_id path
 * parameter so operators can target any tenant. Auth gate:
 * AI_KILL_SWITCH permission (superadmin / kill-switch-authorized roles).
 *
 *   GET  /api/admin/ai/kill-switch/tenants?tenant_id=...
 *   GET  /api/admin/ai/kill-switch/tenants/{tenant_id}/toggles
 *   POST /api/admin/ai/kill-switch/tenants/{tenant_id}/halt
 *   POST /api/admin/ai/kill-switch/tenants/{tenant_id}/restore
 *   POST /api/admin/ai/kill-switch/tenants/{tenant_id}/halt-all
 *   POST /api/admin/ai/kill-switch/tenants/{tenant_id}/restore-all
 */

import type {
  HaltRecord,
  HaltToggle,
  HaltAllResponse,
  RestoreAllResponse,
} from './tenantKillSwitchService';

export type { HaltRecord, HaltToggle, HaltAllResponse, RestoreAllResponse };

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/ai/kill-switch/tenants';

async function _check(res: Response, label: string): Promise<unknown> {
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      detail = res.statusText;
    }
    throw new Error(`${label} failed: ${res.status} — ${detail}`);
  }
  return res.json();
}

export async function listAllActiveHalts(
  authFetch: AuthFetch,
  tenantId?: string,
): Promise<HaltRecord[]> {
  const url = tenantId ? `${BASE}?tenant_id=${encodeURIComponent(tenantId)}` : BASE;
  const res = await authFetch(url);
  return (await _check(res, 'listAllActiveHalts')) as HaltRecord[];
}

export async function listTenantToggles(
  authFetch: AuthFetch,
  tenantId: string,
): Promise<HaltToggle[]> {
  const res = await authFetch(`${BASE}/${tenantId}/toggles`);
  return (await _check(res, 'listTenantToggles')) as HaltToggle[];
}

export async function operatorHalt(
  authFetch: AuthFetch,
  tenantId: string,
  capability_type: string,
  reason?: string,
): Promise<HaltRecord> {
  const res = await authFetch(`${BASE}/${tenantId}/halt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ capability_type, reason: reason || null }),
  });
  return (await _check(res, 'operatorHalt')) as HaltRecord;
}

export async function operatorRestore(
  authFetch: AuthFetch,
  tenantId: string,
  capability_type: string,
  reason?: string,
): Promise<HaltRecord> {
  const res = await authFetch(`${BASE}/${tenantId}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ capability_type, reason: reason || null }),
  });
  return (await _check(res, 'operatorRestore')) as HaltRecord;
}

export async function operatorHaltAll(
  authFetch: AuthFetch,
  tenantId: string,
  reason?: string,
): Promise<HaltAllResponse> {
  const res = await authFetch(`${BASE}/${tenantId}/halt-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || null }),
  });
  return (await _check(res, 'operatorHaltAll')) as HaltAllResponse;
}

export async function operatorRestoreAll(
  authFetch: AuthFetch,
  tenantId: string,
  reason?: string,
): Promise<RestoreAllResponse> {
  const res = await authFetch(`${BASE}/${tenantId}/restore-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason || null }),
  });
  return (await _check(res, 'operatorRestoreAll')) as RestoreAllResponse;
}
